/**
 * 指挥端 → 服务端：任务通联名册同步（group:mission:{id}）
 * 失败只 warn，不打断 UI。
 */
import apiFetch from '../apiFetch';
import { missionIdMatchesMember } from './deviceIdentity';

/** 把 mission.memberIds 尽量映射成 devices.device_unique_id */
export function resolveMissionMemberDeviceIds(memberIds = [], liveMembers = []) {
  const live = Array.isArray(liveMembers) ? liveMembers : [];
  const out = [];
  const seen = new Set();
  (Array.isArray(memberIds) ? memberIds : []).forEach((raw) => {
    const key = String(raw || '').trim();
    if (!key) return;
    const hit = live.find((m) => (
      missionIdMatchesMember(key, m)
      || String(m?.device_unique_id || '') === key
      || String(m?.name || '') === key
    ));
    const id = String(hit?.device_unique_id || key).trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(id);
  });
  return out;
}

/** 通联名册成员 = 该任务 commsLinked===true 的临时组 memberIds 并集（去重） */
export function collectCommsLinkedMemberIds(mission) {
  const groups = Array.isArray(mission?.groups) ? mission.groups : [];
  const out = [];
  const seen = new Set();
  groups.forEach((g) => {
    if (!g || g.commsLinked !== true) return;
    (Array.isArray(g.memberIds) ? g.memberIds : []).forEach((raw) => {
      const key = String(raw || '').trim();
      if (!key || seen.has(key)) return;
      seen.add(key);
      out.push(key);
    });
  });
  return out;
}

export function buildMissionsSyncPayload(missions = [], liveMembers = []) {
  return (Array.isArray(missions) ? missions : []).map((m) => ({
    id: String(m?.id || '').trim(),
    name: String(m?.name || '').trim(),
    // 空数组仍 upsert，整表替换名册，避免关通联后残留旧成员
    member_device_unique_ids: resolveMissionMemberDeviceIds(collectCommsLinkedMemberIds(m), liveMembers)
  })).filter((m) => m.id);
}

let syncTimer = null;
let lastPayloadJson = '';

export async function syncMissionsRosterToServer(missions = [], liveMembers = []) {
  const list = buildMissionsSyncPayload(missions, liveMembers);
  if (!list.length) {
    return { success: true, skipped: true, upserted_count: 0 };
  }
  const payloadJson = JSON.stringify(list);
  if (payloadJson === lastPayloadJson) {
    return { success: true, skipped: true, deduped: true };
  }
  const res = await apiFetch('/api/tracker/missions/sync', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ missions: list })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    const err = new Error(data.error || data.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = data.code;
    err.data = data;
    throw err;
  }
  lastPayloadJson = payloadJson;
  return data;
}

/** debounce 同步；失败 console.warn */
export function scheduleMissionsRosterSync(missions, liveMembers, delayMs = 800) {
  if (syncTimer) clearTimeout(syncTimer);
  const snapMissions = Array.isArray(missions)
    ? missions.map((m) => ({
      id: m.id,
      name: m.name,
      memberIds: Array.isArray(m.memberIds) ? [...m.memberIds] : [],
      groups: Array.isArray(m.groups)
        ? m.groups.map((g) => ({
          id: g?.id,
          name: g?.name,
          commsLinked: g?.commsLinked === true,
          memberIds: Array.isArray(g?.memberIds) ? [...g.memberIds] : []
        }))
        : []
    }))
    : [];
  const snapLive = Array.isArray(liveMembers) ? liveMembers : [];
  syncTimer = setTimeout(() => {
    syncMissionsRosterToServer(snapMissions, snapLive).catch((err) => {
      console.warn('[mission] roster sync failed:', err?.message || err);
    });
  }, delayMs);
}

export async function endMissionRosterOnServer(missionId) {
  const id = String(missionId || '').trim();
  if (!id) return { success: true, skipped: true };
  // 使后续 sync 去重缓存失效
  lastPayloadJson = '';
  const res = await apiFetch('/api/tracker/missions/sync', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ end_mission_ids: [id] })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    const err = new Error(data.error || data.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = data.code;
    throw err;
  }
  return data;
}

export async function endMissionsRosterOnServer(missionIds = []) {
  const ids = (missionIds || []).map((x) => String(x || '').trim()).filter(Boolean);
  if (!ids.length) return { success: true, skipped: true };
  lastPayloadJson = '';
  const res = await apiFetch('/api/tracker/missions/sync', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ end_mission_ids: ids })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    const err = new Error(data.error || data.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = data.code;
    throw err;
  }
  return data;
}